import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Send, Sparkles, RotateCcw, MessageSquareWarning, MessageSquareText, PhoneCall, Calculator as CalcIcon, ClipboardCheck } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { PageTip } from "@/components/Onboarding";

const newSession = () => {
  const id = crypto.randomUUID();
  localStorage.setItem("wu_chat_session", id);
  return id;
};

const MODES = [
  { id: "objection", label: "Handle an objection", icon: MessageSquareWarning, hint: "Paste what the seller said" },
  { id: "followup", label: "Write a follow-up text", icon: MessageSquareText, hint: "Give the lead details and where you left off" },
  { id: "roleplay", label: "Practice a call", icon: PhoneCall, hint: "Say which seller type: motivated, skeptical or price-stuck" },
  { id: "review", label: "Review my deal", icon: CalcIcon, hint: "Drop the numbers: ARV, repairs, asking" },
  { id: "debrief", label: "Debrief my call", icon: ClipboardCheck, hint: "Tell me how the call went" },
];

const EXAMPLES = [
  "Write me a follow-up text to a tired landlord.",
  "How do I structure a Sub-To with $0 down?",
  "Seller wants $200k, comps say $170k. What do I offer?",
];

export default function Coach() {
  const [params, setParams] = useSearchParams();
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("wu_chat_session") || newSession());
  const [mode, setMode] = useState(null);
  const [streaming, setStreaming] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const listRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(140, el.scrollHeight)}px`;
  }, [input]);

  useEffect(() => {
    if (loaded) return;
    api.get("/chat/history", { params: { session_id: sessionId } })
      .then((r) => setMessages(r.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [loaded, sessionId]);

  // Auto-scroll the chat's internal container to the bottom whenever
  // new messages arrive. Scoped to the list — never moves the page itself.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  // Phone: size the chat panel against the visual viewport so the input always
  // lands just above the bottom nav, keyboard open or closed.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const vv = window.visualViewport;

    const fit = () => {
      if (window.innerWidth >= 768) {
        panel.style.height = "";
        return;
      }
      const top = panel.getBoundingClientRect().top;
      const viewportH = vv ? vv.height : window.innerHeight;
      const nav = document.querySelector('[data-testid="mobile-bottom-nav"]');
      const navH = nav ? nav.getBoundingClientRect().height : 0;
      panel.style.height = `${Math.max(240, viewportH - top - navH - 10)}px`;
    };

    fit();
    const t = setTimeout(fit, 350);
    window.addEventListener("resize", fit);
    vv?.addEventListener("resize", fit);
    vv?.addEventListener("scroll", fit);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", fit);
      vv?.removeEventListener("resize", fit);
      vv?.removeEventListener("scroll", fit);
    };
  }, [loaded]);

  useEffect(() => {
    const ask = params.get("ask");
    if (ask) {
      setInput(ask);
      const next = new URLSearchParams(params);
      next.delete("ask");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async (e, override) => {
    e?.preventDefault();
    const text = (override ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    setStreaming("");
    let acc = "";
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("wu_token")}`,
        },
        body: JSON.stringify({ session_id: sessionId, message: text, mode }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalReply = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const chunk of chunks) {
          const line = chunk.trim();
          if (!line.startsWith("data:")) continue;
          const evt = JSON.parse(line.slice(5));
          if (evt.delta) {
            acc += evt.delta;
            setStreaming(acc);
          } else if (evt.error) {
            throw new Error(evt.error);
          } else if (evt.done) {
            finalReply = evt.reply;
          }
        }
      }
      setMessages((m) => [...m, { role: "assistant", content: finalReply ?? acc }]);
    } catch (err) {
      if (acc) {
        setMessages((m) => [...m, { role: "assistant", content: acc }]);
      } else {
        setMessages((m) => [...m, { role: "assistant", content: "Something went wrong. Try again." }]);
      }
    } finally {
      setStreaming("");
      setBusy(false);
    }
  };

  const applyExample = (text) => {
    setInput(text);
    inputRef.current?.focus();
  };

  const reset = () => {
    setSessionId(newSession());
    setMessages([]);
    setLoaded(true);
  };

  return (
    <div className="max-w-3xl mx-auto coach-screen" data-testid="coach-page">
      <div className="coach-static">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <div className="section-tag mb-3"><span className="dot" /> AI Coach</div>
            <h1 className="h-display text-2xl sm:text-4xl">Ask your coach</h1>
            <p className="text-sm mt-2 hidden sm:block" style={{ color: "var(--text-muted)" }}>
              Scripts, objections, contracts, dispo, JV. Get unstuck in seconds.
            </p>
          </div>
          <button
            onClick={reset}
            className="btn-ghost text-xs flex items-center gap-2 shrink-0"
            data-testid="coach-new-chat-button"
          >
            <RotateCcw size={12} /> New chat
          </button>
        </div>

        <PageTip flag="coach" title="Stuck mid-call? Ask away.">
          Drop the objection, the seller's last text, or the deal scenario. Your AI coach throws back scripts, comp framings and next step plays, plus a quick sanity check on your numbers. Use the Calculator when you want the full breakdown.
        </PageTip>
      </div>

      <div
        ref={panelRef}
        className="surface-card flex flex-col overflow-hidden coach-panel"
        style={{ padding: 0 }}
        data-testid="coach-panel"
      >
        <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="coach-messages">
          {messages.length === 0 && (
            <div className="text-sm p-4 rounded-xl" style={{ background: "var(--surface2)", color: "var(--text-muted)", border: "1px solid var(--border)" }} data-testid="coach-welcome">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} style={{ color: "var(--lime)" }} />
                <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>Try asking</span>
              </div>
              <div className="space-y-2">
                {EXAMPLES.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => applyExample(ex)}
                    className="block w-full text-left px-3 py-2 rounded-xl text-sm transition hover:bg-white/5"
                    style={{ border: "1px solid var(--border)", color: "var(--text)" }}
                    data-testid={`coach-example-${i}`}
                  >
                    &ldquo;{ex}&rdquo;
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className="max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed"
                style={m.role === "user"
                  ? { background: "var(--lime)", color: "#0c0c0c", borderBottomRightRadius: 6 }
                  : { background: "var(--surface2)", color: "var(--text)", border: "1px solid var(--border)", borderBottomLeftRadius: 6 }}
                data-testid={`coach-msg-${m.role}-${i}`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div
                className="max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed"
                style={{ background: "var(--surface2)", color: "var(--text)", border: "1px solid var(--border)", borderBottomLeftRadius: 6 }}
                data-testid="coach-typing"
              >
                {streaming || <span className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>Thinking…</span>}
              </div>
            </div>
          )}
          <div />
        </div>

        <div className="flex gap-2 px-3 pt-2 pb-1 overflow-x-auto" style={{ borderTop: "1px solid var(--border)" }} data-testid="coach-modes">
          {MODES.map((m) => {
            const Icon = m.icon;
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => { setMode(active ? null : m.id); inputRef.current?.focus(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition shrink-0"
                style={{
                  background: active ? "var(--lime-glow)" : "var(--surface2)",
                  color: active ? "var(--lime)" : "var(--text-muted)",
                  border: `1px solid ${active ? "var(--border-lime)" : "var(--border)"}`,
                }}
                title={m.hint}
                data-testid={`coach-mode-${m.id}`}
              >
                <Icon size={12} /> {m.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={send} className="coach-form flex items-center gap-2 p-3" style={{ borderTop: "1px solid var(--border)", background: "var(--surface2)" }}>
          <textarea
            ref={inputRef}
            rows={1}
            className="input-base flex-1 text-sm resize-none"
            style={{ minHeight: 44, maxHeight: 140, overflowY: "auto", lineHeight: 1.45, resize: "none" }}
            placeholder={mode ? MODES.find((m) => m.id === mode).hint : "Ask about wholesaling…"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(e);
              }
            }}
            maxLength={2000}
            data-testid="coach-input"
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            className="flex items-center justify-center rounded-full transition hover:scale-105 disabled:opacity-40 shrink-0"
            style={{ width: 40, height: 40, background: "var(--lime)", color: "#0c0c0c" }}
            data-testid="coach-send-button"
          >
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}
